// src/com/home/TopHome.js
import React, {useEffect, useRef, useState} from 'react';
import { styled  } from '@mui/material/styles';
import { Box } from '@mui/system';
import {
    AppBar, CircularProgress, IconButton, ListItemText, MenuItem, MenuList, Tab, Tabs, Tooltip
} from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import TopViewControl from '../../common/TopViewControl';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import {
    CurrentPlanStateColumns, CurrentPlanStateColumnsForChart,
    LIST_CURRENT_PLAN_STAT_WITH_RENT, makeCurrantPlanStateTree
} from '../current_plan_stat_with_rent/CurrentPlanStatWithRentGql';
import { AppObject, AppServiceUrl, AppStyle, AppWord } from '../../value/AppConst';
import { useAlertSetter, useClientSize } from '../../api/app-hook';
//import { currentPlanStatWithRentListRepo, menuTreeRepo, userInfoRepo } from '../../value/AppModel';
import { currentMenuRepo, currentPlanStatWithRentListRepo, menuTreeRepo, userInfoRepo } from '../../value/AppModel';
import { useSnackbar } from 'notistack';
import ValueMisc from '../../value/ValueMisc';
import PieChartWithCenterText from '../../common/PieChartWithCenterText';
import BbsPkg from '../bbs_pkg/BbsPkg';
import BbsIcon from '../../asset/image/bbs.svg';
import TransPlanTodayForStatus, { TransPlanRequestState } from './TransPlanTodayForStatus';

const TopLabelHeight = 42;
const ValueHeight = 36;
const BarHeight = 22;

const TopHomeContainer = styled(Box)({
    display:'flex', flexDirection:'column', height:'100%'
});
const StateContainer = styled(Box)({
    padding: 5, marginBottom: 10,
});
const BbsContainer = styled(Box)({
    flexGrow:1, // borderTop: AppStyle.BorderEEE,
    display: 'flex', padding: 5
});
const BbsSelectorBox = styled(Box)({
    border: AppStyle.BorderCCC, borderRadius: 5, padding: 5, maxWidth: 140
});
const BbsBox = styled(Box)({flexGrow: 1});

// const StateHeader
const StateBox = styled(Box)({display: 'flex'});
const StateDataBox = styled(Box)({});
const DcTypeTree = styled(Box)({display: 'flex'}); // just for naming.
const AnyLabel = styled(Box)({
    display: 'flex', alignItems: 'center', justifyContent: 'center', margin: 1,
    minHeight: ValueHeight, fontSize: '0.9rem'
});
const DcRecordsBox = styled(Box)({flexGrow:1});
const ValueBox = styled(Box)({display:'flex'}); // contains dc name and values (1+5 columns)

const ChartBox = styled(Box)({flexGrow:1});

const BarBox = styled(Box)({
    display: 'inline-flex',
    height: BarHeight,
    minWidth: 0,
    textAlign: 'right',
    alignItems: 'center',
    justifyContent: 'flex-end', // borderRadius: 3,
    fontSize: '0.7rem',
    paddingRight: 2,
    border: '1px solid #77f',
    boxSizing: 'border-box',
    overflow: 'hidden',
    '&:last-of-type': {borderRadius: '0 3px 3px 0'},
    '&:first-of-type': {borderRadius: '3px 0 0 3px'},
    '&:not(:first-of-type)': {borderLeft: '0px'},
});

const StateCountLabel = styled('span')({
    cursor: 'pointer', color: AppStyle.PrimaryRGB
})

const DcTypePieChartBox = styled(Box)({
    display: 'flex', flexDirection: 'column', alignItems: 'center', // header, charts of DCs
    border: AppStyle.BorderCCC, borderRadius: 5,
    margin: '10px 3px',
    '&:first-of-type': {marginLeft: 0},
    '&:last-of-type': {marginRight: 0},
});

const DcTypePieChartHeader = styled(Box)({
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: 42, width: '100%', color: 'white', fontWeight: 'bold',
    borderTopLeftRadius: 5, borderTopRightRadius: 5,
    boxSizing: 'border-box',
});

const DcTypePieChartBody = styled(Box)({
    display: 'flex', alignItems: 'center', paddingTop: 15, paddingBottom: 15,
});

// PieChartBox in DcTypePieChartBody
const PieChartBox = styled(Box)({
    display: 'flex', flexDirection: 'column', alignItems: 'center', // chart, dclabel,
});

const TypeColor = {
    A: AppStyle.PrimaryRGB, // 상온
    F: AppStyle.SuccessRGB, // 냉장
    E: AppStyle.WarnRGB, // Expres
};

const Widths = {
    dcType: 100,
    dc: 80,
    dcTypeAndDc: 100 + 80, 
    // total: 100 + 80 + DcTableInset,
    // value: 90,
};

// const TotalStyle = {
//     backgroundColor: '#ccc',
//     width: Widths.dcTypeAndDc,
//     borderRadius: 3,
// }

// const TotalValueStyle = {
//     backgroundColor: '#ccc',
//     // width: Widths.value,
//     total: 100 + 80 + DcTableInset,
//     borderRadius: 3,
// }

const TabMenu = {Bar:1, Pie:2};

// 홈플러스 외에는 메뉴에 게시판이 포함되지 않음. 강제로하기 위해서 하드코딩. 메뉴 URL들이 바뀔 일은 없을 것이므로. (추가되면...)
const BbsMenus = [
    {
        menuName: "공지사항",
        lastMenu: "1",
        menuUrl: "TAB_Bbs",
        menuId: 11500,
    },
    {
        menuName: "배차현황 게시판",
        lastMenu: "1",
        menuUrl: "TAB_VehBbs",
        menuId: 12000,
    },
    {
        menuName: "업데이트 게시판",
        lastMenu: "1",
        menuUrl: "TAB_BackBbs",
        menuId: 12500,
    },
    {
        menuName: "사용자 게시판",
        lastMenu: "1",
        menuUrl: "TAB_UserBbs",
        menuId: 13000,
    }
]

/**
 * 로그인 하자마자 처음 보이는 화면. 상태정보와 게시판 묶음을 보여준다.
 * 상태정보는 홈플러스 사용자들에게만 보임.
 * @returns 
 */
export default function TopHome() {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const menuTree = useReactiveVar(menuTreeRepo);
    const currentPlanStatWithRentListData = useReactiveVar(currentPlanStatWithRentListRepo);
    const currentMenu = useReactiveVar(currentMenuRepo);
    const [treeAndTotal, setTreeAndTotal] = useState({tree:[], total:{}});
    const [timeLabel, setTimeLabel] = useState('');
    const [tabSelected, setTabSelected] = useState(TabMenu.Bar);
    const [bbsTree, setBbsTree] = useState({bbsSelected:null, siblings:[]});
    const [transPlanTodayForStatusState, setTransPlanTodayForStatusState] = useState({open:false});
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const clientSize = useClientSize();
    const lastReloadRef = useRef(0);

    // ##### Call GraphQL to get List #####
    const [getCurrentPlanStatWithRentList, responseList] = useLazyQuery(LIST_CURRENT_PLAN_STAT_WITH_RENT, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetStatList(data, option)},
		onError: (error) => addAlert({title: '현황정보 조회 오류', message: [error.message]})
    });

    // find menuUrl: "TAB_Bbs"
    // find parent: menuUrl: "NM_BBS"
    useEffect(() => {
        if(sessionInfo.isHomeplusOrEtrace()) {
            // 메인화면 접속(마운트) 시마다 실시간 조회. 단, 직전 조회 후 10초 이내 재마운트는 스킵(연속 메뉴 전환 시 중복 호출 방지).
            /*
            const diff = new Date().getTime()/1000 - currentPlanStatWithRentListData.when;
            if(diff > 10) // || currentPlanStatWithRentListData.records.length === 0)
                getCurrentPlanStatWithRentList();
            */
            const option = ValueMisc.getLocalStorageValue(AppWord.OPTION_LOCALSTORAGE_KEY);
            if(option.homeChartTab)
                setTabSelected(option.homeChartTab===TabMenu.Pie ? TabMenu.Pie : TabMenu.Bar);
        }
    }, [sessionInfo]);


    useEffect(() => {
        // 홈 탭은 AppMain에서 언마운트되지 않고 display/visibility만 토글되므로, 마운트 이펙트만으로는
        // "재방문"을 감지할 수 없다. currentMenuRepo가 홈으로 바뀔 때마다(=홈 탭 재방문 시마다) 실시간 조회.
        // 단, 직전 조회 후 10초 이내 재방문은 스킵(연속 탭 전환 시 중복 호출 방지).
        if(sessionInfo.isHomeplusOrEtrace() && currentMenu.menu.menuUrl === AppObject.HomeMenu.menu.menuUrl) {
            const diff = new Date().getTime()/1000 - currentPlanStatWithRentListData.when;
            if(diff > 10)
                getCurrentPlanStatWithRentList();
        }
    }, [sessionInfo, currentMenu]);


    useEffect(() => {
        if(menuTree && menuTree.length > 0) {
            const bbsMenuTree = ValueMisc.searchParentMenu(AppServiceUrl.BBS, menuTree);
            if(bbsMenuTree && bbsMenuTree.children.length>0) {
                setBbsTree({bbsSelected:bbsMenuTree.children[0], siblings: bbsMenuTree.children});
            }
            else {
                setBbsTree({bbsSelected: BbsMenus[0], siblings: BbsMenus});
            }
        }
    }, [menuTree]);

    useEffect(() => {
        if(currentPlanStatWithRentListData.records.length > 0) {
            const newData = makeCurrantPlanStateTree(currentPlanStatWithRentListData.records);
            setTreeAndTotal(newData);
            const pieTree = {};
            newData.tree.forEach((subTree)=>{
                pieTree[subTree.dcTypeCd] = {
                    dcTypeCd:subTree.dcTypeCd,
                    dcTypeName:subTree.dcTypeName,
                    dcList:[]
                };
                subTree.dcList.forEach((dcRec)=>{
                    const pieData = CurrentPlanStateColumnsForChart.map((field)=>{
                        return {name:field.label, value:dcRec[field.valueKey]};
                    })

                    pieTree[subTree.dcTypeCd].dcList.push({
                        dcCd: dcRec.dcCd,
                        dcLabel: dcRec.dcLabel,
                        pieData: pieData,
                    });
                })
            });
        }
    }, [currentPlanStatWithRentListData]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetStatList = (data, clientOption) => {
        if(data.currentPlanStatWithRentList) {
            const now = new Date();
            currentPlanStatWithRentListRepo({when:now.getTime()/1000, records:data.currentPlanStatWithRentList});
            lastReloadRef.current = now.getTime() / 1000;
            setTimeLabel(`${now.getHours()}시 ${now.getMinutes()}분`);
        }
    };

    const onSelectChartTab = (event, newValue) => {
        setTabSelected(newValue);
        const option = ValueMisc.getLocalStorageValue(AppWord.OPTION_LOCALSTORAGE_KEY);
        option.homeChartTab = newValue;
        ValueMisc.saveLocalStorageValue(AppWord.OPTION_LOCALSTORAGE_KEY, option);
    };

    const onClickOpenTransPlanTodayForStatus = (rec, reqState) => {
        setTransPlanTodayForStatusState({
            open:true,
            dcCd:rec.dcCd,
            reqState, // 'left', 'assigned', 'waiting'
            dcTypeName:rec.dcTypeName,
            dcName:rec.dcLabel
        });
    };

    const onClickCloseTransPlanTodayForStatus = () => {
        setTransPlanTodayForStatusState({...transPlanTodayForStatusState, open:false});
    };

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 10) {
            // Too short to reload
            enqueueSnackbar('10초 이내 재조회는 삼가해 주세요', {variant:'info', autoHideDuration:1500});
        }
        else getCurrentPlanStatWithRentList();
    };

    // BBS
    const onClickBbsMenu = (menu) => {
        if(!Boolean(bbsTree.bbsSelected) || menu.menuId !== bbsTree.bbsSelected.menuId)
            setBbsTree({...bbsTree, bbsSelected:menu});
    }

    const renderDcState = (rec) => {
        const valueBgColor = rec.index % 2 === 0 ? '#eee' : '#fff';
        // const valueStyle = {width: Widths.value, backgroundColor: valueBgColor, fontSize: '0.8rem'};
        const valueStyle = {
            flex: '1 1 0%', minWidth: 0, backgroundColor: valueBgColor, fontSize: '0.8rem',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        };
        return(
            <ValueBox key={rec.dcCd}>
                <AnyLabel
                    style={{
                        backgroundColor: TypeColor[rec.dcTypeCd],
                        color: 'white',
                        width: Widths.dc,
                        borderRadius: 3,
                    }}
                >
                    {rec.dcLabel}
                </AnyLabel>
                <AnyLabel style={valueStyle}>
                    {rec.transRequest}
                    {rec.usePercent ? null : ` (${rec.rented})`}
                </AnyLabel>
                <AnyLabel style={valueStyle}>
                    {rec.planned}
                    {rec.usePercent ? ` (${rec.plannedRate}%)` : ` (${rec.plannedRent})`}
                </AnyLabel>
                <AnyLabel style={valueStyle}>
                    {rec.leftDc}
                    {rec.usePercent ? ` (${rec.leftRate}%)` : ` (${rec.leftRent})`}
                </AnyLabel>
                <AnyLabel style={valueStyle}>
                    {
                        rec.assignDc > 0 && !rec.usePercent
                        ?
                        <StateCountLabel
                            onClick={()=>onClickOpenTransPlanTodayForStatus(rec, TransPlanRequestState.assigned)}
                        >
                        {rec.assignDc
                        }</StateCountLabel>
                        :
                        <span>{rec.assignDc}</span>
                    }
                    {rec.usePercent ? ` (${rec.assignRate}%)` : ` (${rec.assignRent})`}
                </AnyLabel>
                <AnyLabel style={valueStyle}>
                    {
                        rec.waitDc > 0 && !rec.usePercent
                        ?
                        <StateCountLabel
                            onClick={()=>onClickOpenTransPlanTodayForStatus(rec, TransPlanRequestState.waiting)}
                        >
                        {rec.waitDc}</StateCountLabel>
                        :
                        <span>{rec.waitDc}</span>
                    }
                    {rec.usePercent ? ` (${rec.waitRate}%)` : ` (${rec.waitRent})`}
                </AnyLabel>
            </ValueBox>
        );
    };

    const renderDcTypeStates = (subTree) => {
        return (
            <DcTypeTree key={subTree.dcTypeCd}>
                <AnyLabel
                    style={{
                        backgroundColor: TypeColor[subTree.dcTypeCd],
                        color: 'white',
                        width: Widths.dcType,
                        borderRadius: 3,
                    }}
                >
                    {subTree.dcTypeName}
                </AnyLabel>
                <DcRecordsBox>
                    {
                        subTree.dcList.map((dcRec)=>{
                            return renderDcState(dcRec);
                        })
                    }
                </DcRecordsBox>
            </DcTypeTree>
        );
    };

    const renderPieChart = (dcRec, groupDcCount) => {
        const pieData = CurrentPlanStateColumnsForChart.map((field)=>{
            return {label:field.label, value:dcRec[field.valueKey], color: field.color};
        });
        const clientWidth = clientSize.width > AppStyle.MaxDataBodyWidth ? AppStyle.MaxDataBodyWidth : clientSize.width;
        //const chartWidth = Math.round(clientWidth / currentPlanStatWithRentListData.records.length) - 5;
        // 파이차트는 dcType(상온/냉장/Express)별로 별도 박스에 나뉘어 그려지므로, 그 박스 하나에 들어갈
        // 차종 수(groupDcCount)로 나눠야 각 그룹의 행이 절반 너비를 온전히 채운다. 전체 센터 수로 나누면
        // 그룹마다 실제보다 훨씬 작게 그려져 절반씩 나뉜 것처럼 보이지 않는다.
        const chartWidth = Math.round((clientWidth / 2) / groupDcCount) - 5;

        return(
            <PieChartBox key={dcRec.dcCd}>
                <Box style={{flexGrow:1}}>
                    <PieChartWithCenterText list={pieData} width={chartWidth} />
                </Box>
                <Box style={{
                        marginTop:10, textAlign: 'center',
                        color: TypeColor[dcRec.dcTypeCd],
                        fontWeight: 'bold'
                    }}
                >
                    {dcRec.dcLabel}
                </Box>
            </PieChartBox>
        );
    }

    const renderAllPieChartBox = () => {
        return(
            <ChartBox
                style={{display:'flex', flexWrap:'wrap'}}
            >
                {
                    treeAndTotal.tree.map((subTree) => {
                        return(
                            <DcTypePieChartBox key={subTree.dcTypeCd}>
                                <DcTypePieChartHeader
                                    style={{backgroundColor: TypeColor[subTree.dcTypeCd]}}
                                >
                                    {subTree.dcTypeName}
                                </DcTypePieChartHeader>
                                <DcTypePieChartBody>
                                    {
                                        subTree.dcList.map((dcRec) => {
                                            return renderPieChart(dcRec, subTree.dcList.length);
                                        })
                                    }
                                </DcTypePieChartBody>
                            </DcTypePieChartBox>
                        )
                    })
                }
            </ChartBox>
        );
    };

    const renderBarRecord = (rec) => {
        const total = rec.planned + rec.leftDc + rec.assignDc + rec.waitDc;
        const bgColor = rec.index % 2 === 0 ? '#eee' : '#fff';
        return(
            <Box
                key={rec.dcCd}
                style={{
                    height: ValueHeight,
                    minWidth: 0,
                    margin: '1px 0',
                    backgroundColor: bgColor,
                    display: 'flex',
                    alignItems: 'center'
                }}
            >
                {/* <Box style={{width: Widths.dcTypeAndDc}} /> */}
                {
                    CurrentPlanStateColumnsForChart
                    .filter((field)=>rec[field.valueKey] > 0)
                    .map((field)=>{
                        const value = rec[field.valueKey];
                        const percent = Math.round(value * 10000 / total) / 100;
                        return(
                            <BarBox key={field.valueKey}
                                style={{
                                    flexGrow: percent,
                                    backgroundColor: field.color,
                                }}
                            >
                                <Tooltip title={`${rec.dcLabel} ${field.label}`}>
                                    <span>{value}</span>
                                </Tooltip>
                            </BarBox>
                        );
                    })
                }
            </Box>
        );
    };

    const renderBarChart = () => {
        return(
            <ChartBox style={{display: 'flex', flexDirection: 'column'}}>
                {
                    treeAndTotal.tree.map((subTree)=>{
                        return(
                            <React.Fragment key={subTree.dcTypeCd}>
                            {
                                subTree.dcList.map((dcRec)=>{
                                    return renderBarRecord(dcRec);
                                })
                            }
                            </React.Fragment>
                        );
                    })
                }
                <Box
                    style={{flexGrow: 1, minHeight: ValueHeight, margin: '1px 0', backgroundColor: '#ccc'}}
                >
                    {/* <Box style={{width: Widths.dcTypeAndDc}} />
                    <Box style={{flex: '1 1 0%', minWidth: 0, backgroundColor: '#ccc'}}>&nbsp;</Box> */}
                </Box>
            </ChartBox>
        );
    };

    const loadingAny = responseList.loading;

    const renderBbsContainer = () => {
        return(
                    <BbsContainer>
                        <BbsSelectorBox>
                            <Box>
                                <img src={BbsIcon} alt='BBS' width={60} />
                            </Box>
                            <MenuList>
                                {
                                    bbsTree.siblings.map((menu, index)=>{
                                        return(
                                                <MenuItem key={menu.menuId}
                                                    onClick={()=>onClickBbsMenu(menu)}
                                                    disabled={
                                                        !Boolean(bbsTree.bbsSelected)
                                                        || menu.menuId === bbsTree.bbsSelected.menuId
                                                    }
                                                    sx={{
                                                        '&.Mui-disabled': {
                                                        color: AppStyle.PrimaryRGB,
                                                        fontWeight: 'bold',
                                                        opacity: 1,
                                                        },
                                                    }}
                                                >
                                                    <ListItemText>
                                                        {menu.menuName}
                                                    </ListItemText>
                                                </MenuItem>
                                        )
                                    })
                                }
                            </MenuList>
                            {
                                bbsTree.siblings.length===0
                                &&
                                <Box
                                    style={{
                                        padding:10, textAlign: 'center',
                                        color: '#7ff', fontSize: '0.8rem'
                                    }}
                                >
                                    잠시만 기다려 주세요.
                                </Box>
                            }
                        </BbsSelectorBox>
                        <BbsBox>
                            {
                                bbsTree.bbsSelected
                                ? <BbsPkg menu={bbsTree.bbsSelected} parentMenu={null} /> : null
                            }
                        </BbsBox>
                    </BbsContainer>
        );
    }

    if(!sessionInfo.isHomeplusOrEtrace()) {
        return(
            <TopViewControl>
                <TopHomeContainer>
                    {renderBbsContainer()}
                </TopHomeContainer>
            </TopViewControl>
        );
    }

    const totalLabelStyle = {backgroundColor: '#ccc', width: Widths.dcTypeAndDc, borderRadius: 3};
    const totalValueStyle = {backgroundColor: '#ccc', flex: '1 1 0%', minWidth: 0, borderRadius: 3};



    return(
        <TopViewControl>
            <TopHomeContainer>
                <StateContainer>
                    <StateBox>
                        <StateDataBox style={{flex: '1 1 0%', minWidth: 0}}>
                            <Box style={{
                                boxSizing: 'border-box',
                                border: AppStyle.BorderCCC, borderRadius: 5, padding: 8,
                                display: 'flex', flexDirection: 'column', alignItems: 'stretch',
                        }}>
                        <DcTypeTree style={{minHeight: TopLabelHeight}}>
                            <AnyLabel
                                style={{width: Widths.dcTypeAndDc}}
                            >
                                {timeLabel}
                                {
                                    loadingAny ?
                                    <CircularProgress size={20} />
                                    :
                                    <IconButton
                                        color={AppStyle.mui.inherit}
                                        onClick={onClickReload}
                                    >
                                        <CachedIcon />
                                    </IconButton>
                                }
                            </AnyLabel>
                            <DcRecordsBox>
                                <ValueBox style={{height:'100%'}}>
                                    {
                                        CurrentPlanStateColumns.map((col) => {
                                            return(
                                                <AnyLabel
                                                    key={col.valueKey}
                                                    style={{
                                                        backgroundColor: col.color,
                                                        flex: '1 1 0%', minWidth: 0,
                                                        borderRadius: 3,
                                                    }}
                                                >
                                                    {col.label}
                                                </AnyLabel>
                                            );
                                        })
                                    }
                                </ValueBox>
                            </DcRecordsBox>
                        </DcTypeTree>                        
                                {
                                    treeAndTotal.tree.map((subTree)=>{
                                        return renderDcTypeStates(subTree);
                                    })
                                }
                                    <DcTypeTree>
                                        <AnyLabel style={totalLabelStyle}>Total</AnyLabel>
                                    <DcRecordsBox>
                                        <ValueBox>
                                            <AnyLabel style={totalValueStyle}>
                                                {ValueMisc.int2str(treeAndTotal.total.transRequest || 0)}
                                            </AnyLabel>
                                            <AnyLabel style={totalValueStyle}>
                                                {ValueMisc.int2str(treeAndTotal.total.planned || 0)}
                                            </AnyLabel>
                                            <AnyLabel style={totalValueStyle}>
                                                {ValueMisc.int2str(treeAndTotal.total.leftDc || 0)}
                                            </AnyLabel>
                                            <AnyLabel style={totalValueStyle}>
                                                {ValueMisc.int2str(treeAndTotal.total.assignDc || 0)}
                                            </AnyLabel>
                                            <AnyLabel style={totalValueStyle}>
                                                    {ValueMisc.int2str(treeAndTotal.total.waitDc || 0)}
                                                </AnyLabel>
                                            </ValueBox>
                                        </DcRecordsBox>
                                    </DcTypeTree>
                                </Box>
                            </StateDataBox>
                            <Box style={{flex: '1 1 0%', minWidth: 0, marginLeft: 10, display: 'flex'}}>
                            <Box style={{
                                boxSizing: 'border-box',
                                border: AppStyle.BorderCCC, borderRadius: 5, padding: 8,
                                display: 'flex', flexDirection: 'column', alignItems: 'stretch',
                                flexGrow: 1,
                            }}>
                                <AppBar position="static" sx={{height: TopLabelHeight}}>
                                    <Tabs
                                        value={tabSelected}
                                        onChange={onSelectChartTab}
                                        variant="fullWidth"
                                        textColor={AppStyle.mui.inherit}
                                        indicatorColor={AppStyle.mui.secondary}
                                        sx={{minHeight: TopLabelHeight, height: TopLabelHeight}}
                                    >
                                        <Tab label="막대차트" value={TabMenu.Bar} sx={{minHeight: TopLabelHeight, height: TopLabelHeight}} />
                                        <Tab label="파이차트" value={TabMenu.Pie} sx={{minHeight: TopLabelHeight, height: TopLabelHeight}} />
                                    </Tabs>
                                </AppBar>
                                {
                                    tabSelected === TabMenu.Bar ?
                                    renderBarChart() :
                                    renderAllPieChartBox()
                                }
                            </Box>
                        </Box>
                        {/* {
                            tabSelected === TabMenu.Bar ?
                            renderBarChart() :
                            renderAllPieChartBox()
                        } */}
                    </StateBox>
                </StateContainer>
                {renderBbsContainer()}
            </TopHomeContainer>
            <TransPlanTodayForStatus
                open={transPlanTodayForStatusState.open}
                onClose={onClickCloseTransPlanTodayForStatus}
                dcCd={transPlanTodayForStatusState.dcCd}
                reqState={transPlanTodayForStatusState.reqState}
                dcTypeName={transPlanTodayForStatusState.dcTypeName}
                dcName={transPlanTodayForStatusState.dcName}
            />
        </TopViewControl>
    );
}